import React from 'react'

import { Box } from '../box'
import { Pressable } from '../pressable'
import { Text } from '../text'
import { chipSeverityContainer, chipSeverityDot, type SeverityTone } from './variants'

export type ChipSeverityProps = {
    label: string
    tone?: SeverityTone
    /** Selected chips gain a tone-coloured border. */
    isSelected?: boolean
    isDisabled?: boolean
    onPress?: () => void
    className?: string
    testID?: string
}

/**
 * Severity chip matching the Figma redesign (AOS-603): a neutral `highlight`
 * pill with a status-coloured dot + label. The `tone` drives the dot colour and
 * the selected border; hover lightens the surface; disabled dims to 40%.
 * Presentational and controlled — `onPress` toggles selection in the parent.
 * Without `onPress` the chip renders as a plain read-only Box (no button role,
 * no pointer cursor).
 */
export const ChipSeverity: React.FC<ChipSeverityProps> = ({
    label,
    tone = 'neutral',
    isSelected,
    isDisabled,
    onPress,
    className,
    testID,
}) => {
    const container = chipSeverityContainer({
        tone,
        isSelected: Boolean(isSelected),
        isDisabled: Boolean(isDisabled),
        isInteractive: Boolean(onPress) && !isDisabled,
        class: className,
    })

    const content = (
        <>
            <Box className={chipSeverityDot({ tone })} />
            <Text weight='semiBold'>{label}</Text>
        </>
    )

    if (!onPress) {
        return (
            <Box className={container} accessibilityLabel={label} testID={testID}>
                {content}
            </Box>
        )
    }

    return (
        <Pressable
            className={container}
            disabled={Boolean(isDisabled)}
            onPress={isDisabled ? undefined : onPress}
            accessibilityRole='button'
            accessibilityState={{ selected: Boolean(isSelected), disabled: Boolean(isDisabled) }}
            accessibilityLabel={label}
            testID={testID}
        >
            {content}
        </Pressable>
    )
}

ChipSeverity.displayName = 'ChipSeverity'
